// Notes, decision 90 v2 (Carla, 2026-10-12): the page you land on is the Personal Journal mockup's
// "Your entries" section (design/personal/mockups/journal.html), rebuilt on the v2 kit, and NOTHING
// above it (no tiles). Presentational: Notes.js owns the notes, the saving and the address.
//
//   the section bar      "📝 Your notes" on a pastel bar, with a Grid / List switch
//   chips                All, the five built-ins (General, Meetings, Clients, Projects, Ideas), the
//                        person's own categories, each with its count, and ⭐ Favourites. With All,
//                        favourites float to the top within the month.
//   search, linked to    search runs across every month and every page; "Linked to" narrows to the
//                        notes linked to one client or one project
//   month navigator      ← previous month | Month ▾ Year ▾ | next month →, on the month a note was
//                        last edited; it opens on the month of the most recently edited note, so a
//                        person never lands on an empty month while they have notes
//   cards                a large day number with the month and weekday, the title, a 3-line preview,
//                        a footer with the category left and the link pill right; badges: ⭐, From
//                        Aidey, Shared. A page with pages inside says how many.
//   pages inside pages   only top-level notes are cards ("N pages inside" on the card); a search shows
//                        every page that matches, at any depth, with the page it sits in.
//   load more            after 24
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, ChipGroup, EmptyState, Menu, SearchInput, Select, SegmentedControl, Skeleton, Banner, v,
} from '../../ui';
import {
  categoryChips, chipKeyOf, editedAt, monthKeyOf, MONTHS, noteTitle, notePreview,
} from './noteContent';
import './notes.css';
import './notesBrowse.css';

const PAGE = 24;
const VIEW_KEY = 'aidey_notes_view';
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const time = (iso) => { const t = Date.parse(iso || ''); return Number.isNaN(t) ? 0 : t; };
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function readView() {
  try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
}

const Dot = ({ colour }) => <span className="nt-dot" style={{ '--nt-dot': colour || v('pastel-neutral') }} aria-hidden="true" />;

// The day block: "12" with "OCT" over "MON".
function Day({ iso }) {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return <span className="nt-card__day" aria-hidden="true">–</span>;
  return (
    <span className="nt-card__date" aria-hidden="true">
      <span className="nt-card__day">{d.getDate()}</span>
      <span className="nt-card__mondow"><span>{MON[d.getMonth()]}</span><span className="nt-card__dow">{DOW[d.getDay()]}</span></span>
    </span>
  );
}

function Badges({ note, shared }) {
  return (
    <span className="nt-card__badges">
      {note.favourite && <span className="nt-badge" title="Favourite"><span aria-hidden="true">⭐</span><span className="ui-visually-hidden">Favourite</span></span>}
      {note.fromAidey && <span className="nt-badge nt-badge--aidey">From Aidey</span>}
      {(shared || note.sharedWithFamily) && <span className="nt-badge nt-badge--shared">{shared ? `From ${note.sharedByName || 'family'}` : 'Shared'}</span>}
    </span>
  );
}

// One note as a card (grid) or a row (list). The card's whole face opens it; its ⋯ holds the rest.
function NoteCard({ note, list, chip, link, inside, insideOf, onOpen, actions, shared }) {
  const title = noteTitle(note);
  const preview = notePreview(note);
  const when = editedAt(note);
  const whenLabel = when ? new Date(when).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const name = [title, insideOf && `Inside ${insideOf}`, shared && `from ${note.sharedByName || 'family'}`, whenLabel && `edited ${whenLabel}`, chip?.name, link && `linked to ${link.label}`].filter(Boolean).join(', ');
  return (
    <li className={`nt-card${list ? ' nt-card--row' : ''}`} data-testid="note-card">
      <button type="button" className="nt-card__open" onClick={onOpen} aria-label={name} title={title}>
        <span className="nt-card__head">
          <Day iso={when} />
          <Badges note={note} shared={shared} />
        </span>
        <span className="nt-card__title">{note.emoji ? <span aria-hidden="true">{note.emoji} </span> : null}{title}</span>
        {insideOf && <span className="nt-card__inside">Inside {insideOf}</span>}
        {preview && <span className="nt-card__preview">{preview}</span>}
        <span className="nt-card__foot">
          <span className="nt-card__cat">{chip ? <><Dot colour={chip.colour || v(chip.token || 'pastel-neutral')} />{chip.name}</> : null}
            {inside > 0 && <span className="nt-card__pages">· {plural(inside, 'page')} inside</span>}
          </span>
          {link && <span className={`nt-pill nt-pill--${link.kind}`}><span aria-hidden="true">{link.icon} </span>{link.label}</span>}
        </span>
      </button>
      {actions && actions.length > 0 && (
        <span className="nt-card__menu">
          <Menu items={actions} label={`More for ${title}`} title={title} buttonClassName="nt-card__more ui-hit" />
        </span>
      )}
    </li>
  );
}

/**
 * notes        the person's own notes (visible ones), any depth
 * shared       notes shared with them (Personal), or null when not asked for
 * categories   their note categories
 * chips        the category chips, in her order (Notes passes them; else made from categories)
 * linkOf(note) -> { kind, icon, label } | null   (client, project or task pill)
 * linkOptions  [{ value: 'contact:<id>' | 'project:<id>', label }] for "Linked to"
 */
export default function NotesBrowse({
  notes, categories, chips: chipsIn, linkOf, linkOptions = [], loading, shared, sharedState, sharedError, onRetryShared,
  onOpen, onOpenShared, onNew, actionsFor, filterRequest,
}) {
  const [view, setView] = useState(readView);
  const [chip, setChip] = useState('all');
  const [q, setQ] = useState('');
  const [linked, setLinked] = useState('');
  const [shown, setShown] = useState(PAGE);
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* storage unavailable: the switch lasts for the visit */ } }, [view]);
  // Another page asked for a filter ("Notes filters by linked client or project").
  useEffect(() => { if (filterRequest) { setLinked(filterRequest); setChip('all'); } }, [filterRequest]);

  // The page passes its chips (one source of truth with the editor and Manage categories); on its own
  // it makes them from the categories.
  const chips = useMemo(() => chipsIn || categoryChips(categories), [chipsIn, categories]);
  const chipByKey = useMemo(() => new Map(chips.map((c) => [c.key, c])), [chips]);
  const byId = useMemo(() => new Map(notes.map((n) => [n.id, n])), [notes]);
  const isTop = (n) => !n.parentId || !byId.has(n.parentId);
  const insideCount = useMemo(() => {
    const kids = new Map();
    for (const n of notes) if (n.parentId && byId.has(n.parentId)) kids.set(n.parentId, [...(kids.get(n.parentId) || []), n.id]);
    const count = (id, seen = new Set()) => (kids.get(id) || []).reduce((s, k) => (seen.has(k) ? s : (seen.add(k), s + 1 + count(k, seen))), 0);
    return (id) => count(id);
  }, [notes, byId]);

  // The month shown: the most recently edited note's, until the person moves it.
  const latest = useMemo(() => notes.reduce((m, n) => (time(editedAt(n)) > time(m) ? editedAt(n) : m), null), [notes]);
  const now = new Date();
  const [month, setMonth] = useState(null);   // { y, m } | null (= the latest note's month)
  const at = month || (() => { const d = latest ? new Date(latest) : now; return { y: d.getFullYear(), m: d.getMonth() }; })();
  const atKey = `${at.y}-${at.m}`;
  const atNow = at.y > now.getFullYear() || (at.y === now.getFullYear() && at.m >= now.getMonth());
  const step = (k) => { const d = new Date(at.y, at.m + k, 1); setMonth({ y: d.getFullYear(), m: d.getMonth() }); setShown(PAGE); };
  const label = (k) => { const d = new Date(at.y, at.m + k, 1); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
  const years = useMemo(() => {
    const ys = new Set([now.getFullYear(), at.y]);
    for (const n of notes) { const d = new Date(editedAt(n) || ''); if (!Number.isNaN(d.getTime())) ys.add(d.getFullYear()); }
    return [...ys].sort((a, b) => b - a);
  }, [notes, at.y]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = q.trim().toLowerCase();
  const paged = !query;
  const linkMatch = (n) => {
    if (!linked) return true;
    const [kind, id] = linked.split(':');
    return kind === 'contact' ? String(n.contactId || '') === id : String(n.projectId ?? '') === id;
  };
  const inChip = (n) => (chip === 'all' ? true : chip === 'favourites' ? !!n.favourite : chipKeyOf(n, chips) === chip);
  const matches = (n) => !query || `${n.title || ''} ${n.text || ''} ${noteTitle(n)} ${notePreview(n, 100000)}`.toLowerCase().includes(query);

  // Counts follow what the chips would show this month (or across months while searching).
  const pool = notes.filter((n) => (query ? true : isTop(n)) && linkMatch(n) && matches(n) && (!paged || monthKeyOf(editedAt(n)) === atKey));
  const countFor = (key) => pool.filter((n) => (key === 'all' ? true : key === 'favourites' ? n.favourite : chipKeyOf(n, chips) === key)).length;
  const list = pool.filter(inChip).sort((a, b) => {
    if (chip === 'all' && !!b.favourite !== !!a.favourite) return b.favourite ? 1 : -1;
    return time(editedAt(b)) - time(editedAt(a));
  });
  const visible = list.slice(0, shown);

  const chipOptions = [
    { value: 'all', label: `All (${countFor('all')})` },
    ...chips.map((c) => ({ value: c.key, label: `${c.name} (${countFor(c.key)})`, icon: <Dot colour={c.colour || v(c.token || 'pastel-neutral')} /> })),
    { value: 'favourites', label: `⭐ Favourites (${countFor('favourites')})` },
  ];
  const latestLabel = latest ? `${MONTHS[new Date(latest).getMonth()]} ${new Date(latest).getFullYear()}` : null;

  return (
    <section className="nt-browse" aria-labelledby="nt-yours">
      <div className="nt-sectionbar">
        <h2 id="nt-yours" className="nt-sectionbar__title"><span aria-hidden="true">📝 </span>Your notes</h2>
        <SegmentedControl label="Show notes as" value={view} onChange={setView}
          options={[{ value: 'grid', label: 'Grid' }, { value: 'list', label: 'List' }]} />
      </div>

      <div className="nt-filters">
        <ChipGroup label="Category" value={chip} onChange={(k) => { setChip(k); setShown(PAGE); }} scroll options={chipOptions} />
        <div className="nt-filters__row">
          <SearchInput label="Search notes" placeholder="Search notes…" value={q} onChange={(e) => { setQ(e.target.value); setShown(PAGE); }} className="nt-search" />
          {linkOptions.length > 0 && (
            <Select aria-label="Linked to" className="nt-linked" value={linked} onChange={(e) => { setLinked(e.target.value); setShown(PAGE); }}>
              <option value="">Linked to anything</option>
              {linkOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          )}
        </div>
      </div>

      {paged && (
        <nav className="nt-months" aria-label="Choose a month">
          <Button variant="secondary" size="sm" onClick={() => step(-1)} aria-label={`Previous month, ${label(-1)}`}>← <span className="nt-months__lbl">{label(-1)}</span></Button>
          <span className="nt-months__pick">
            <select aria-label="Month" className="nt-months__sel" value={at.m} onChange={(e) => { setMonth({ y: at.y, m: Number(e.target.value) }); setShown(PAGE); }}>
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <select aria-label="Year" className="nt-months__sel" value={at.y} onChange={(e) => { setMonth({ y: Number(e.target.value), m: at.m }); setShown(PAGE); }}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </span>
          <Button variant="secondary" size="sm" onClick={() => step(1)} disabled={atNow} aria-label={`Next month, ${label(1)}`}><span className="nt-months__lbl">{label(1)}</span> →</Button>
        </nav>
      )}

      {loading && !notes.length ? (
        <Skeleton lines={4} label="Loading your notes" />
      ) : !notes.length ? (
        <EmptyState icon="📝" title="Your first note" action={<Button variant="create" className="ui-addtask" onClick={onNew}>+ New note</Button>}>
          Meeting notes, plans, ideas: write anything. Start blank or from a template.
        </EmptyState>
      ) : !list.length ? (
        <div className="nt-empty" role="status">
          {query ? `No notes match "${q.trim()}".`
            : `No ${chip === 'favourites' ? 'favourite ' : chip !== 'all' ? `${chipByKey.get(chip)?.name || ''} ` : ''}notes in ${MONTHS[at.m]} ${at.y}${linked ? ' linked to that' : ''}.`}
          {!query && latestLabel && atKey !== monthKeyOf(latest) && (
            <> <button type="button" className="nt-linkbtn" onClick={() => setMonth(null)}>Go to {latestLabel}</button></>
          )}
          {!query && <> <button type="button" className="nt-linkbtn" onClick={onNew}>Write one</button></>}
        </div>
      ) : (
        <ul className={view === 'list' ? 'nt-rows' : 'nt-grid'} aria-label="Notes">
          {visible.map((n) => {
            const parent = !isTop(n) ? byId.get(n.parentId) : null;
            return (
              <NoteCard key={n.id} note={n} list={view === 'list'} chip={chipByKey.get(chipKeyOf(n, chips))} link={linkOf(n)}
                inside={insideCount(n.id)} insideOf={parent ? noteTitle(parent) : null}
                onOpen={() => onOpen(n.id)} actions={actionsFor(n)} />
            );
          })}
        </ul>
      )}
      {list.length > shown && (
        <div className="nt-more"><Button variant="secondary" onClick={() => setShown((s) => s + PAGE)}>Load more ({list.length - shown} more)</Button></div>
      )}

      {shared !== null && (sharedState === 'loading' || sharedState === 'failed' || shared.length > 0) && (
        <section className="nt-shared" aria-labelledby="nt-shared-title">
          <h2 id="nt-shared-title" className="nt-sectionbar nt-sectionbar--lilac nt-sectionbar__title">Shared with me</h2>
          {sharedState === 'loading' && !shared.length ? (
            <Skeleton lines={2} label="Loading notes shared with you" />
          ) : sharedState === 'failed' ? (
            <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={onRetryShared}>Try again</Button>}>{sharedError}</Banner>
          ) : (
            <ul className={view === 'list' ? 'nt-rows' : 'nt-grid'} aria-label="Notes shared with me">
              {shared.map((n) => {
                const parent = n.parentId && shared.find((x) => x.id === n.parentId);
                return <NoteCard key={n.id} note={n} list={view === 'list'} shared insideOf={parent ? noteTitle(parent) : null} onOpen={() => onOpenShared(n.id)} />;
              })}
            </ul>
          )}
        </section>
      )}
    </section>
  );
}
